import { Link } from '@tanstack/react-router';
import { ArrowRight, Banknote, BarChart3, Boxes, Factory, ScanLine, ShieldCheck, Users } from 'lucide-react';
import { MarketingShell } from './MarketingShell';
import { useCms, cmsText, cmsList, cmsRich } from '../../api/cms';
import { SINGLE_FALLBACKS, LIST_FALLBACKS } from '../../content/fallbacks';

// Icons stay fixed in code and pair with the CMS-editable `features.groups`
// list items by position (see content/registry.ts note).
const GROUP_ICONS = [Boxes, ScanLine, Users, Banknote, Factory, BarChart3];

export function Features() {
  const { data: cms } = useCms();
  const intro = SINGLE_FALLBACKS['features.intro']!;
  const groups = cmsList(cms, 'features.groups', LIST_FALLBACKS['features.groups']!);
  // Empty fallback by design (like the two-tone hero headlines elsewhere) — an
  // unset override keeps the exact original JSX below (a plain `font-medium`
  // span, not cmsRich's bold styling, so the untouched page stays byte-identical).
  const dataBannerOverride = cmsText(cms, 'features.dataBanner', 'text', '');

  return (
    <MarketingShell>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-10 text-center">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand">{cmsText(cms, 'features.intro', 'eyebrow', intro.eyebrow!)}</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 max-w-3xl mx-auto leading-[1.05]">
          {cmsText(cms, 'features.intro', 'headline', intro.headline!)}
        </h1>
        <p className="mt-4 text-lg text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed">
          {cmsText(cms, 'features.intro', 'subhead', intro.subhead!)}
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {groups.map((g, i) => {
          const Icon = GROUP_ICONS[i] ?? GROUP_ICONS[GROUP_ICONS.length - 1]!;
          const items = Array.isArray(g.items) ? g.items : [];
          return (
            <div key={i} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-6">
              <span className="w-10 h-10 grid place-items-center rounded-lg bg-brand-subtle dark:bg-brand/15 text-brand dark:text-brand-dm">
                <Icon className="w-5 h-5" />
              </span>
              <h2 className="mt-4 font-semibold text-zinc-950 dark:text-zinc-50">{g.title}</h2>
              <ul className="mt-3 space-y-2">
                {items.map((it, ii) => (
                  <li key={ii} className="flex items-start gap-2 text-sm text-zinc-600 dark:text-zinc-300">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand/60 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-900/40 p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <ShieldCheck className="w-8 h-8 text-brand dark:text-brand-dm shrink-0" />
            {dataBannerOverride.trim() !== '' ? (
              <p className="text-zinc-700 dark:text-zinc-200">{cmsRich(dataBannerOverride)}</p>
            ) : (
              <p className="text-zinc-700 dark:text-zinc-200">
                Your data stays in <span className="font-medium">your own database</span> — exportable any time, never sold.
              </p>
            )}
          </div>
          <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand/90 transition whitespace-nowrap">
            Start free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
